/* ============================================================================
   Enshield Design DNA — STATUS LANGUAGE (React)
   ----------------------------------------------------------------------------
   The ten states Enshield actually has, each bound to one of five semantic
   families. Adding a state means choosing a meaning, not inventing a colour.

   Every state here corresponds to something the platform genuinely does. This
   is the marketing site borrowing the product's vocabulary, not inventing
   capabilities: a visitor should be able to recognise these later in the
   dashboard. If a state is ever added here that the product cannot produce,
   the site is fabricating a feature.
   ========================================================================== */
import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Icon, type IconName } from './icons';

export type StatusFamily = 'signal' | 'secure' | 'attention' | 'critical' | 'neutral';

export type StatusName =
  | 'connected'
  | 'monitoring'
  | 'protected'
  | 'issue-detected'
  | 'action-required'
  | 'submitted'
  | 'in-review'
  | 'resolved'
  | 'failed'
  | 'expired';

type StatusSpec = {
  label: string;
  family: StatusFamily;
  icon: IconName;
  /** Only a genuinely live state may pulse. */
  live?: boolean;
};

export const STATUS: Record<StatusName, StatusSpec> = {
  connected: { label: 'Connected', family: 'secure', icon: 'plug' },
  monitoring: { label: 'Monitoring', family: 'signal', icon: 'sync', live: true },
  protected: { label: 'Protected', family: 'secure', icon: 'shield-check' },
  'issue-detected': { label: 'Issue detected', family: 'attention', icon: 'alert' },
  'action-required': { label: 'Action required', family: 'attention', icon: 'bell' },
  submitted: { label: 'Submitted', family: 'neutral', icon: 'file' },
  'in-review': { label: 'In review', family: 'neutral', icon: 'search' },
  resolved: { label: 'Resolved', family: 'secure', icon: 'check' },
  failed: { label: 'Failed', family: 'critical', icon: 'alert' },
  expired: { label: 'Expired', family: 'neutral', icon: 'clock' },
};

export function Status({
  name,
  size = 'sm',
  label,
  className,
}: {
  name: StatusName;
  size?: 'sm' | 'lg';
  /** Override the copy without changing the state's meaning or colour. */
  label?: ReactNode;
  className?: string;
}) {
  const spec = STATUS[name];
  const reducedMotion = useReducedMotion();

  return (
    <motion.span
      className={['st', size === 'lg' && 'st--lg', className].filter(Boolean).join(' ')}
      data-family={spec.family}
      data-status={name}
      /*
       * `layout` is what makes a change of state read as one object changing
       * rather than two pills swapping places: the pill's width animates to
       * fit the new label, and the animation is interruptible — change it
       * again mid-flight and it retargets from where it is rather than
       * snapping and restarting. That interruptibility is the quality worth
       * having; a transition that cannot be interrupted feels like a video.
       */
      layout={reducedMotion ? false : true}
      transition={reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 34, mass: 0.7 }}
    >
      {/* A live state shows a pulsing dot; every other state shows its own
          icon, so the pill never relies on colour alone to be readable.
          Keyed on the state so the icon cross-fades when the state changes
          instead of popping. */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={name}
          className="st__icon"
          initial={reducedMotion ? false : { opacity: 0, scale: 0.72 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={reducedMotion ? undefined : { opacity: 0, scale: 0.72 }}
          transition={reducedMotion ? { duration: 0 } : { duration: 0.16, ease: [0.22, 0.78, 0.34, 1] }}
        >
          {spec.live ? <span className="st__pulse" /> : <Icon name={spec.icon} />}
        </motion.span>
      </AnimatePresence>
      <motion.span layout={reducedMotion ? false : 'position'} className="st__label">
        {label ?? spec.label}
      </motion.span>
    </motion.span>
  );
}

export function StatusRow({ names, size, className }: { names: StatusName[]; size?: 'sm' | 'lg'; className?: string }) {
  return (
    <div className={['st-row', className].filter(Boolean).join(' ')}>
      {names.map((name) => <Status key={name} name={name} size={size} />)}
    </div>
  );
}
